import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { mainnet } from 'viem/chains'

import {
  infoSkyApiUrl,
  SPETH_POINTS_MULTIPLIER,
  SPUSDC_POINTS_MULTIPLIER,
  SPUSDT_POINTS_MULTIPLIER,
  SUSDS_POINTS_MULTIPLIER,
} from '../../../consts'
import {
  chroniclePointsFarmAddress,
  lendingPoolAddress,
  skyUsdsFarmAddress,
  sparkVaultV2SpusdtAddress,
  spkUsdsFarmAddress,
} from '../../../contracts-generated'
import { farmStablecoinsEntryGroup } from '../../constants'
import {
  SPARK_VAULTS_V2_ENABLED,
  SPARK_VAULTS_V2_PYUSD_ENABLED,
  STUSDS_ACCOUNT_ENABLED,
  USDC_ACCOUNT_ENABLED,
} from '../../flags'
import { ChainConfigEntry, SavingsStatsItem } from '../../types'
import { sortSavingsAccounts } from '../../utils'
import { chainLogo, tokenLogos } from './assets'
import { createTokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { skySavingsGeneralStatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/skySavings'
import { sparkVaultsV2StatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/sparkVaultsV2'
import { stusdsGeneralStatsQueryOptions } from '@/features/savings/data/general-stats-query/stusds'
import { skySavingsBackedByOptions } from '@/features/savings/data/savings-charts/backed-by-query/skySavingsBackedByQuery'
import { sparkVaultV2EthBackedByQueryOptionsFactory } from '@/features/savings/data/savings-charts/backed-by-query/sparkVaultV2EthBackedByQuery'
import { sparkVaultV2StableBackedByQueryOptionsFactory } from '@/features/savings/data/savings-charts/backed-by-query/sparkVaultV2StableBackedByQuery'
import { sparkVaultV2LiquidityQueryOptionsFactory } from '@/features/savings/data/savings-charts/liquidity-query/sparkVaultV2LiquidityQuery'
import {
  sdaiSavingsRateQueryOptions,
  susdcSavingsRateQueryOptions,
  susdsSavingsRateQueryOptions,
} from '@/features/savings/data/savings-charts/savings-rate-query/skySavings'
import { sparkVaultsV2SavingsRateQueryOptions } from '@/features/savings/data/savings-charts/savings-rate-query/sparkVaultsV2'
import { stusdsSavingsRateQueryOptions } from '@/features/savings/data/savings-charts/savings-rate-query/stusds'
import {
  mainnetSavingsDaiConverterQuery,
  mainnetSavingsUsdcConverterQuery,
  mainnetSavingsUsdsConverterQuery,
} from '@/features/savings/logic/savings-converters/mainnetSavingsConverter'
import {
  sparkVaultV2SpethConverterQuery,
  sparkVaultV2SppyusdConverterQuery,
  sparkVaultV2SpusdcConverterQuery,
  sparkVaultV2SpusdtConverterQuery,
} from '@/features/savings/logic/savings-converters/sparkVaultV2Converter'
import { stusdsConverterQueryOptions } from '@/features/savings/logic/savings-converters/stusdsConverter'
import {
  fetchEzethOracleInfo,
  fetchRethOracleInfo,
  fetchRsethOracleInfo,
  fetchWeethOracleInfo,
  fetchWstethOracleInfoMainnet,
} from '@/features/sparklend/common/data/oracles/oracleInfoFetchers'
import {
  historicBorrowRateMainnetQueryOptions,
  historicSupplyRateMainnetQueryOptions,
} from '@/features/sparklend/market-details/data/market-charts/mainnet'

const dai = new TokenDefinition({
  symbol: TokenSymbol('DAI'),
  name: 'DAI Stablecoin',
  decimals: 18,
  address: CheckedAddress('0x6b175474e89094c44da98b954eedeac495271d0f'),
  logo: tokenLogos.dai,
})

const usdc = new TokenDefinition({
  symbol: TokenSymbol('USDC'),
  name: 'Circle USD',
  decimals: 6,
  address: CheckedAddress('0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'),
  logo: tokenLogos.usdc,
})

const sdai = new TokenDefinition({
  symbol: TokenSymbol('sDAI'),
  name: 'Savings Dai',
  decimals: 18,
  address: CheckedAddress('0x83f20f44975d03b1b09e64809b757c47f942beea'),
  logo: tokenLogos.sdai,
})

const usds = new TokenDefinition({
  symbol: TokenSymbol('USDS'),
  name: 'USDS Stablecoin',
  decimals: 18,
  address: CheckedAddress('0xdC035D45d973E3EC169d2276DDab16f1e407384F'),
  logo: tokenLogos.usds,
})

const susds = new TokenDefinition({
  symbol: TokenSymbol('sUSDS'),
  name: 'Savings USDS',
  decimals: 18,
  address: CheckedAddress('0xa3931d71877C0E7a3148CB7Eb4463524FEc27fbD'),
  logo: tokenLogos.susds,
})

const susdc = new TokenDefinition({
  symbol: TokenSymbol('sUSDC'),
  name: 'Spark Savings USDC',
  decimals: 18,
  address: CheckedAddress('0xBc65ad17c5C0a2A4D159fa5a503f4992c7B545FE'),
  logo: tokenLogos.susdc,
})

const sky = new TokenDefinition({
  symbol: TokenSymbol('SKY'),
  name: 'SKY',
  decimals: 18,
  address: CheckedAddress('0x56072C95FAA701256059aa122697B133aDEd9279'),
  logo: tokenLogos.sky,
})

const spk = new TokenDefinition({
  symbol: TokenSymbol('SPK'),
  name: 'Spark',
  decimals: 18,
  address: CheckedAddress('0xc20059e0317DE91738d13af027DfC4a50781b066'),
  logo: tokenLogos.spk,
})

const usdt = new TokenDefinition({
  symbol: TokenSymbol('USDT'),
  name: 'Tether USD',
  decimals: 6,
  address: CheckedAddress('0xdAC17F958D2ee523a2206206994597C13D831ec7'),
  logo: tokenLogos.usdt,
})

const weth = new TokenDefinition({
  symbol: TokenSymbol('WETH'),
  name: 'Wrapped Ether',
  decimals: 18,
  address: CheckedAddress('0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2'),
  logo: tokenLogos.weth,
})

const eth = new TokenDefinition({
  symbol: TokenSymbol('ETH'),
  name: 'Ether',
  decimals: 18,
  address: CheckedAddress.EEEE(),
  logo: tokenLogos.eth,
})

const pyusd = new TokenDefinition({
  symbol: TokenSymbol('PYUSD'),
  name: 'PayPal USD',
  decimals: 6,
  address: CheckedAddress('0x6c3ea9036406852006290770BEdFcAbA0e23A0e8'),
  logo: tokenLogos.pyusd,
})

const stusds = new TokenDefinition({
  symbol: TokenSymbol('stUSDS'),
  name: 'Staked USDS',
  decimals: 18,
  address: CheckedAddress('0x99CD4Ec3f88A45940936F469E4bB72A2A701EEB9'),
  logo: tokenLogos.stusds,
})

const spusdc = new TokenDefinition({
  symbol: TokenSymbol('spUSDC'),
  name: 'Spark Savings USDC',
  decimals: 6,
  address: CheckedAddress('0x28B3a8fb53B741A8Fd78c0fb9A6B2393d896a43d'),
  logo: tokenLogos.spusdc,
})

const spusdt = new TokenDefinition({
  symbol: TokenSymbol('spUSDT'),
  name: 'Spark Savings USDT',
  decimals: 6,
  address: CheckedAddress('0xe2e7a17dFf93280dec073C995595155283e3C372'),
  logo: tokenLogos.spusdt,
})

const speth = new TokenDefinition({
  symbol: TokenSymbol('spETH'),
  name: 'Spark Savings ETH',
  decimals: 18,
  address: CheckedAddress('0xfE6eb3b609a7C8352A241f7F3A21CEA4e9209B8f'),
  logo: tokenLogos.speth,
})

const sppyusd = new TokenDefinition({
  symbol: TokenSymbol('spPYUSD'),
  name: 'Spark Savings PYUSD',
  decimals: 6,
  address: CheckedAddress('0x80128DbB9f07b93DDE62A6daeadb69ED14a7D354'),
  logo: tokenLogos.sppyusd,
})

const wbtc = new TokenDefinition({
  symbol: TokenSymbol('WBTC'),
  name: 'Wrapped BTC',
  decimals: 8,
  address: CheckedAddress('0x2260fac5e5542a773aa44fbcfedf7c193bc2c599'),
  logo: tokenLogos.wbtc,
})

const wsteth = new TokenDefinition({
  symbol: TokenSymbol('wstETH'),
  name: 'Lido Staked ETH',
  decimals: 18,
  address: CheckedAddress('0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0'),
  logo: tokenLogos.wsteth,
})

const reth = new TokenDefinition({
  symbol: TokenSymbol('rETH'),
  name: 'Rocket Pool Staked ETH',
  decimals: 18,
  address: CheckedAddress('0xae78736Cd615f374D3085123A210448E74Fc6393'),
  logo: tokenLogos.reth,
})

const weeth = new TokenDefinition({
  symbol: TokenSymbol('weETH'),
  name: 'Ether.fi Staked ETH',
  decimals: 18,
  address: CheckedAddress('0xcd5fe23c85820f7b72d0926fc9b05b43e359b7ee'),
  logo: tokenLogos.weeth,
})

const cbbtc = new TokenDefinition({
  symbol: TokenSymbol('cbBTC'),
  name: 'Coinbase Wrapped BTC',
  decimals: 8,
  address: CheckedAddress('0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf'),
  logo: tokenLogos.cbbtc,
})

const lbtc = new TokenDefinition({
  symbol: TokenSymbol('LBTC'),
  name: 'Lombard Staked BTC',
  decimals: 8,
  address: CheckedAddress('0x8236a87084f8B84306f72007F36F2618A5634494'),
  logo: tokenLogos.lbtc,
})

const tbtc = new TokenDefinition({
  symbol: TokenSymbol('tBTC'),
  name: 'Threshold BTC',
  decimals: 18,
  address: CheckedAddress('0x18084fbA666a33d37592fA2633fD49a74DD93a88'),
  logo: tokenLogos.tbtc,
})

const ezeth = new TokenDefinition({
  symbol: TokenSymbol('ezETH'),
  name: 'Renzo Restaked ETH',
  decimals: 18,
  address: CheckedAddress('0xbf5495Efe5DB9ce00f80364C8B423567e58d2110'),
  logo: tokenLogos.ezeth,
})

const rseth = new TokenDefinition({
  symbol: TokenSymbol('rsETH'),
  name: 'Kelp DAO Restaked ETH',
  decimals: 18,
  address: CheckedAddress('0xA1290d69c65A6Fe4DF752f95823fae25cB99e5A7'),
  logo: tokenLogos.rseth,
})

const cle = new TokenDefinition({
  symbol: TokenSymbol('CLE'),
  name: 'Chronicle',
  decimals: 18,
  address: CheckedAddress.ZERO(),
  logo: tokenLogos.cle,
})

const gno = new TokenDefinition({
  symbol: TokenSymbol('GNO'),
  name: 'Gnosis Token',
  decimals: 18,
  address: CheckedAddress('0x6810e776880C02933D47DB1b9fc05908e5386b96'),
  logo: tokenLogos.gno,
})

const red = new TokenDefinition({
  symbol: TokenSymbol('RED'),
  name: 'RED Token',
  decimals: 18,
  address: CheckedAddress('0xc43C6bfeDA065fE2c4c11765Bf838789bd0BB5dE'),
  logo: tokenLogos.red,
})

export const mainnetConfig: ChainConfigEntry = {
  originChainId: mainnet.id,
  meta: {
    name: 'Ethereum',
    logo: chainLogo,
  },
  tokenDefinitionRepository: createTokenDefinitionRepository([
    dai,
    usdc,
    sdai,
    usds,
    susds,
    susdc,
    sky,
    spk,
    usdt,
    weth,
    eth,
    pyusd,
    stusds,
    spusdc,
    spusdt,
    speth,
    sppyusd,
    wbtc,
    wsteth,
    reth,
    weeth,
    cbbtc,
    lbtc,
    tbtc,
    ezeth,
    rseth,
    cle,
    gno,
    red,
  ]),
  nativeAssetInfo: {
    nativeAssetName: 'Ether',
    wrappedNativeAssetSymbol: weth.symbol,
    wrappedNativeAssetAddress: weth.address,
    nativeAssetSymbol: eth.symbol,
    minRemainingNativeAssetBalance: NormalizedNumber(0.001),
    nativeAssetLogo: tokenLogos.eth,
  },
  permitSupport: {
    [dai.address]: false,
    [sdai.address]: true,
    [usdc.address]: false,
    [usdt.address]: false,
    [wbtc.address]: false,
    [weth.address]: false,
    [wsteth.address]: true,
    [reth.address]: false,
    [gno.address]: false,
    [usds.address]: true,
    [susds.address]: true,
    [weeth.address]: false,
    [cbbtc.address]: false,
    [lbtc.address]: true,
    [ezeth.address]: false,
    [pyusd.address]: false,
    [rseth.address]: false,
    [tbtc.address]: true,
  },
  tokensWithMalformedApprove: [usdt.address],
  tokensRequiringResetApproval: [usdt.address],
  contractsRequiringUsdtResetApproval: [lendingPoolAddress[mainnet.id], sparkVaultV2SpusdtAddress[mainnet.id]],
  sparkLend: {
    defaultAssetToBorrow: usds.symbol,
    easyBorrowTokensToBorrow: [dai.symbol, usds.symbol, usdc.symbol, usdt.symbol, pyusd.symbol],
    sllManagedMarkets: [dai.symbol, usds.symbol, usdc.symbol, pyusd.symbol],
    easyBorrow: true,
    supplyRateQueryOptions: historicSupplyRateMainnetQueryOptions,
    borrowRateQueryOptions: historicBorrowRateMainnetQueryOptions,
    oracles: {
      [weth.symbol]: {
        type: 'market-price',
        providedBy: ['chainlink', 'chronicle', 'redstone'],
      },
      [wbtc.symbol]: {
        type: 'market-price',
        providedBy: ['chainlink'],
      },
      [wsteth.symbol]: {
        type: 'yielding-fixed',
        baseAssetSymbol: weth.symbol,
        providedBy: ['chainlink', 'chronicle', 'redstone'],
        oracleFetcher: fetchWstethOracleInfoMainnet,
      },
      [reth.symbol]: {
        type: 'yielding-fixed',
        baseAssetSymbol: weth.symbol,
        providedBy: ['chainlink', 'chronicle', 'redstone'],
        oracleFetcher: fetchRethOracleInfo,
      },
      [weeth.symbol]: {
        type: 'yielding-fixed',
        baseAssetSymbol: weth.symbol,
        providedBy: ['chainlink', 'chronicle', 'redstone'],
        oracleFetcher: fetchWeethOracleInfo,
      },
      [usds.symbol]: {
        type: 'fixed',
      },
      [usdc.symbol]: {
        type: 'fixed',
      },
      [usdt.symbol]: {
        type: 'fixed',
      },
      [dai.symbol]: {
        type: 'fixed',
      },
      [pyusd.symbol]: {
        type: 'fixed',
      },
      [cbbtc.symbol]: {
        type: 'underlying-asset',
        asset: 'BTC',
        providedBy: ['chainlink', 'chronicle', 'redstone'],
      },
      [lbtc.symbol]: {
        type: 'underlying-asset',
        asset: 'BTC',
        providedBy: ['chainlink', 'chronicle', 'redstone'],
      },
      [tbtc.symbol]: {
        type: 'underlying-asset',
        asset: 'BTC',
        providedBy: ['chainlink', 'chronicle', 'redstone'],
      },
      [ezeth.symbol]: {
        type: 'yielding-fixed',
        baseAssetSymbol: weth.symbol,
        providedBy: ['chainlink', 'chronicle', 'redstone'],
        oracleFetcher: fetchEzethOracleInfo,
      },
      [rseth.symbol]: {
        type: 'yielding-fixed',
        baseAssetSymbol: weth.symbol,
        providedBy: ['chainlink', 'chronicle', 'redstone'],
        oracleFetcher: fetchRsethOracleInfo,
      },
      [gno.symbol]: {
        type: 'market-price',
        providedBy: ['chainlink'],
      },
    },
  },
  savings: {
    priceConfigs: {
      [dai.symbol]: { type: 'fixed-usd' },
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [pyusd.symbol]: { type: 'fixed-usd' },
      [usdt.symbol]: { type: 'fixed-usd' },
      [sdai.symbol]: { type: 'vault' },
      [susds.symbol]: { type: 'vault' },
      [weth.symbol]: { type: 'chronicle', oracleAddress: CheckedAddress('0x46ef0071b1E2fF6B42d36e5A177EA43Ae5917f4E') },
      [eth.symbol]: { type: 'chronicle', oracleAddress: CheckedAddress('0x46ef0071b1E2fF6B42d36e5A177EA43Ae5917f4E') },
      ...(USDC_ACCOUNT_ENABLED ? { [susdc.symbol]: { type: 'vault' as const, assetsDecimals: 6 } } : {}),
      ...(STUSDS_ACCOUNT_ENABLED ? { [stusds.symbol]: { type: 'vault' as const } } : {}),
      ...(SPARK_VAULTS_V2_ENABLED
        ? {
            [spusdc.symbol]: { type: 'vault' as const },
            [spusdt.symbol]: { type: 'vault' as const },
            [speth.symbol]: { type: 'vault' as const },
          }
        : {}),
      ...(SPARK_VAULTS_V2_PYUSD_ENABLED ? { [sppyusd.symbol]: { type: 'vault' as const } } : {}),
    },
    accounts: sortSavingsAccounts([
      ...(SPARK_VAULTS_V2_ENABLED
        ? [
            {
              savingsToken: spusdc.symbol,
              underlyingToken: usdc.symbol,
              supportedAssets: [usdc.symbol],
              fetchConverterQuery: sparkVaultV2SpusdcConverterQuery,
              savingsRateQueryOptions: sparkVaultsV2SavingsRateQueryOptions,
              stats: {
                queryOptions: sparkVaultsV2StatsQueryOptionsFactory('mainnet-spusdc'),
                items: ['tvl', 'users', 'deposit-cap', 'liquidity-spark-vaults-v2'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: sparkVaultV2StableBackedByQueryOptionsFactory('spusdc'),
              liquidityChartQueryOptions: sparkVaultV2LiquidityQueryOptionsFactory('spusdc'),
              sparkPointsMultiplier: SPUSDC_POINTS_MULTIPLIER,
              historyCutoff: new Date('2025-10-19T00:00:00.000Z'),
            },
            {
              savingsToken: spusdt.symbol,
              underlyingToken: usdt.symbol,
              supportedAssets: [usdt.symbol],
              fetchConverterQuery: sparkVaultV2SpusdtConverterQuery,
              savingsRateQueryOptions: sparkVaultsV2SavingsRateQueryOptions,
              stats: {
                queryOptions: sparkVaultsV2StatsQueryOptionsFactory('mainnet-spusdt'),
                items: ['tvl', 'users', 'deposit-cap', 'liquidity-spark-vaults-v2'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: sparkVaultV2StableBackedByQueryOptionsFactory('spusdt'),
              liquidityChartQueryOptions: sparkVaultV2LiquidityQueryOptionsFactory('spusdt'),
              sparkPointsMultiplier: SPUSDT_POINTS_MULTIPLIER,
              historyCutoff: new Date('2025-10-19T00:00:00.000Z'),
            },
            {
              savingsToken: speth.symbol,
              underlyingToken: weth.symbol,
              supportedAssets: [eth.symbol, weth.symbol],
              fetchConverterQuery: sparkVaultV2SpethConverterQuery,
              savingsRateQueryOptions: sparkVaultsV2SavingsRateQueryOptions,
              stats: {
                queryOptions: sparkVaultsV2StatsQueryOptionsFactory('mainnet-speth'),
                items: ['tvl', 'users', 'deposit-cap', 'liquidity-spark-vaults-v2'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: sparkVaultV2EthBackedByQueryOptionsFactory,
              liquidityChartQueryOptions: undefined,
              sparkPointsMultiplier: SPETH_POINTS_MULTIPLIER,
              historyCutoff: new Date('2025-10-19T00:00:00.000Z'),
            },
          ]
        : []),
      ...(SPARK_VAULTS_V2_PYUSD_ENABLED
        ? [
            {
              savingsToken: sppyusd.symbol,
              underlyingToken: pyusd.symbol,
              supportedAssets: [pyusd.symbol],
              fetchConverterQuery: sparkVaultV2SppyusdConverterQuery,
              savingsRateQueryOptions: sparkVaultsV2SavingsRateQueryOptions,
              stats: {
                queryOptions: sparkVaultsV2StatsQueryOptionsFactory('mainnet-sppyusd'),
                items: ['tvl', 'users', 'deposit-cap', 'liquidity-spark-vaults-v2'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: sparkVaultV2StableBackedByQueryOptionsFactory('sppyusd'),
              liquidityChartQueryOptions: undefined,
              sparkPointsMultiplier: undefined,
              historyCutoff: new Date('2025-12-14T00:00:00.000Z'),
            },
          ]
        : []),
      ...(USDC_ACCOUNT_ENABLED
        ? [
            {
              savingsToken: susdc.symbol,
              underlyingToken: usdc.symbol,
              supportedAssets: [usdc.symbol],
              fetchConverterQuery: mainnetSavingsUsdcConverterQuery,
              savingsRateQueryOptions: susdcSavingsRateQueryOptions,
              stats: {
                queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susdc.symbol),
                items: ['tvl', 'users', 'liquidity-sky-psm'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: skySavingsBackedByOptions,
              liquidityChartQueryOptions: undefined,
              sparkPointsMultiplier: undefined,
            },
          ]
        : []),
      {
        savingsToken: susds.symbol,
        underlyingToken: usds.symbol,
        supportedAssets: [usds.symbol, usdc.symbol, dai.symbol],
        fetchConverterQuery: mainnetSavingsUsdsConverterQuery,
        savingsRateQueryOptions: susdsSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susds.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: SUSDS_POINTS_MULTIPLIER,
      },
      {
        savingsToken: sdai.symbol,
        underlyingToken: dai.symbol,
        supportedAssets: [dai.symbol],
        fetchConverterQuery: mainnetSavingsDaiConverterQuery,
        savingsRateQueryOptions: sdaiSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(sdai.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: undefined,
      },
      ...(STUSDS_ACCOUNT_ENABLED
        ? [
            {
              savingsToken: stusds.symbol,
              underlyingToken: usds.symbol,
              supportedAssets: [usds.symbol],
              fetchConverterQuery: stusdsConverterQueryOptions,
              savingsRateQueryOptions: stusdsSavingsRateQueryOptions,
              stats: {
                queryOptions: stusdsGeneralStatsQueryOptions,
                items: ['tvl', 'users', 'liquidity-stusds'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: undefined,
              liquidityChartQueryOptions: undefined,
              sparkPointsMultiplier: undefined,
            },
          ]
        : []),
    ]),
    psmStables: [usdc.symbol, usds.symbol, dai.symbol],
  },
  legacyFarms: {
    priceConfigs: {
      [dai.symbol]: { type: 'fixed-usd' },
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [sdai.symbol]: { type: 'vault' },
      [susds.symbol]: { type: 'vault' },
      [sky.symbol]: { type: 'chronicle', oracleAddress: CheckedAddress('0xc2ffbbDCCF1466Eb8968a846179191cb881eCdff') },
    },
    configs: [
      {
        rewardType: 'token',
        address: skyUsdsFarmAddress[mainnet.id],
        entryAssetsGroup: {
          type: 'stablecoins',
          name: 'Stablecoins',
          assets: [dai.symbol, usdc.symbol, usds.symbol, sdai.symbol, susds.symbol],
        },
        rewardToken: sky.symbol,
        historyCutoff: new Date('2024-09-17T00:00:00.000Z'),
      },
      {
        // Chronicle farm
        rewardType: 'points',
        address: chroniclePointsFarmAddress[mainnet.id],
        entryAssetsGroup: farmStablecoinsEntryGroup[mainnet.id],
        historyCutoff: new Date('2024-09-17T00:00:00.000Z'),
        rewardPoints: cle,
      },
    ],
    getFarmDetailsApiUrl: (address, page) =>
      `${infoSkyApiUrl}/api/v1/farms/${address.toLowerCase()}/historic/${page ? `?p=${page}` : ''}`,
  },
  spk: {
    priceConfigs: {
      [spk.symbol]: { type: 'chronicle', oracleAddress: CheckedAddress('0x552116E747a5b386B2E7EEd4371002359cf2A574') },
      [usds.symbol]: { type: 'fixed-usd' },
      [usdc.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'vault' },
      [dai.symbol]: { type: 'fixed-usd' },
      [sdai.symbol]: { type: 'vault' },
      ...(USDC_ACCOUNT_ENABLED ? { [susdc.symbol]: { type: 'vault' as const, assetsDecimals: 6 } } : {}),
    },
    farmConfig: {
      rewardType: 'token',
      address: spkUsdsFarmAddress[mainnet.id],
      // @note: next field doesn't matter for SPK farm but is left for backward compatibility
      entryAssetsGroup: {
        type: 'stablecoins',
        name: 'Stablecoins',
        assets: [usds.symbol],
      },
      rewardToken: spk.symbol,
      historyCutoff: new Date('2025-06-30T00:00:00.000Z'),
      getFarmDetailsApiUrl: (address, page) =>
        `${infoSkyApiUrl}/api/v1/farms/${address.toLowerCase()}/historic/${page ? `?p=${page}` : ''}`,
    },
  },
  points: {
    priceConfigs: {
      [spk.symbol]: { type: 'chronicle', oracleAddress: CheckedAddress('0x552116E747a5b386B2E7EEd4371002359cf2A574') },
    },
  },
  sparkRewards: {},
  swap: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [dai.symbol]: { type: 'fixed-usd' },
    },
  },
}
